import { Currency } from "../../../tables/types/types"
import { VatCondition } from "../../administration"
import { Item, PaymentStatus } from "../../operations"
import { IdentifierType } from "../../system"
import { Address } from "../../../features/address"
import { DataTypeEvent } from "../../../features/events"
import { FiscalDocumentStatus, Perception } from "../../fiscal-documents"
import { DeliveryData, DeliveryStatus } from "../../deliveries"

export type Budget = {
    companyId: string
    id: string
    code: number
    items: Item[]
    date: string
    currency: Currency
    customerId: string
    customerTypeId: string
    sellerId: string
    archived?: boolean
}

export type SalePaymentItem = {
    paymentTypeId?: string
    destinationId?: string
    creditInFavor?: boolean
    isRounding?: boolean
    amount: number
    paymentDate: string
    receiptUrl?: string
}

export type InternalNote = {
    text: string
    date: string
}

export type SaleOrder = {
    companyId: string
    id: string
    code: number
    codeStr: string
    creationDate: string
    items: Item[]
    currency: Currency
    paymentTypeId: string
    paymentTypeYield: number
    paymentItems: SalePaymentItem[]
    paidAmount: number
    receivedAmount?: number
    // Amount received above what was owed, credited to the customer credit in favor (not included in paidAmount nor receivedAmount)
    overpaidAmount?: number
    // Sum of the operation adjustment notes (credit notes add, debit notes subtract). What the customer owes is the total minus this amount
    adjustedAmount?: number
    sellerId: string
    customerId: string
    customerTypeId: string
    initialCredit?: number
    deliveryData?: DeliveryData
    deliveryStatus: DeliveryStatus
    paymentStatus: PaymentStatus
    invoiceStatus: FiscalDocumentStatus
    branchId: string
    latePayment?: boolean
    notes?: string
    events?: DataTypeEvent[]
    perceptions?: Perception[]
    internalNotes?: InternalNote[]
    archived?: boolean
    cancelled?: boolean
    negativeStateUserId?: string
    negativeStateAt?: string
}

export enum HoardType {
    FINANCIAL = 'Financial',
    PRODUCT = 'Product'
}

export enum InvoiceMode {
    DELIVERED = 'Delivered',
    TOTAL = 'Total'
}

export type InvoiceItemQuantity = {
    itemId: string
    quantity: number
}

export type InvoiceInFlight = {
    fiscalDocumentId: string
    mode: InvoiceMode
    itemQuantities: InvoiceItemQuantity[]
    fullInvoice?: boolean
}

export type HoardInvoiceStats = {
    fullInvoiceIssued?: boolean
    lastMode?: InvoiceMode
}

export type PricesList = {
    [key: string]: number
}

// A price corrected in the frozen prices list of a financial hoard
export type HoardPriceCorrection = {
    date: string
    productId: string
    previousPrice: number
    newPrice: number
    userId?: string
    // Units already delivered and invoiced when the price was corrected
    deliveredQuantity: number
    invoicedQuantity: number
    // Credit given back to the hoard balance: what the delivered units consumed above the correct price
    returnedBalance: number
    // Fiscal credit note on the invoiced units (it doesn't add balance: the corrected price already gave it back)
    adjustmentNoteId?: string
    adjustmentNoteCode?: number
}

export type SpecialDiscountItem = {
    productId: string
    listPrice: number
    specialDiscountYield: number
    finalPrice: number
}

export type HoardOrder = {
    companyId: string
    id: string
    code: number
    codeStr: string
    type: HoardType
    value?: number
    // Financial hoards: pending credit written off when closing (credited to the customer or not). It is discounted from
    // the pending credit so it can't be consumed again if the hoard gets partially delivered after closing (e.g. a return)
    unusedCredit?: number
    vatTaxYield?: number
    items: Item[]
    currency: Currency
    creationDate: string
    dueDate: string
    paymentTypeId: string
    paymentTypeYield: number
    paymentItems: SalePaymentItem[]
    paidAmount: number
    receivedAmount?: number
    // Amount received above what was owed, credited to the customer credit in favor (not included in paidAmount nor receivedAmount)
    overpaidAmount?: number
    // Sum of the operation adjustment notes (credit notes add, debit notes subtract). What the customer owes is the total minus this amount
    adjustedAmount?: number
    sellerId: string
    customerId: string
    customerTypeId: string
    initialCredit?: number
    deliveryData?: DeliveryData
    deliveryStatus: DeliveryStatus
    paymentStatus: PaymentStatus
    invoiceStatus: FiscalDocumentStatus
    invoiceStats?: HoardInvoiceStats
    invoiceInFlight?: InvoiceInFlight
    pricesList: PricesList
    priceCorrections?: HoardPriceCorrection[]
    branchId: string
    latePayment?: boolean
    notes?: string
    specialDiscountItems?: SpecialDiscountItem[]
    events?: DataTypeEvent[]
    perceptions?: Perception[]
    internalNotes?: InternalNote[]
    archived?: boolean
    cancelled?: boolean
    negativeStateUserId?: string
    negativeStateAt?: string
}

export type SaleOperation = SaleOrder | HoardOrder

export type ProductYield = {
    productId: string
    yield: number
    isExcluded?: boolean
}

export type CustomerType = {
    companyId: string
    id: string
    name: string
    yield: number
    productYields: ProductYield[]
    archived?: boolean
    negativeStateUserId?: string
    negativeStateAt?: string
    baseCustomerTypeId?: string
}

export enum CustomerCategory {
    NATURAL = 'Natural',
    LEGAL = 'Legal'
}

export type CustomerCreditAccount = {
    creditLimit: number
    creditDays: number
}

export type Customer = {
    companyId: string
    id: string
    name: string
    identifierType: IdentifierType
    identifier: number
    category: CustomerCategory
    customerTypeId: string
    emails: string[]
    grossIncomeProvinces?: string[]
    phones: number[]
    mainAddress?: Address
    deliveryAddresses: Address[]
    vatCondition: VatCondition
    contactName?: string
    contactPhone?: number
    creditAccount?: CustomerCreditAccount
    creditInFavor?: number
    archived?: boolean
    negativeStateUserId?: string
    negativeStateAt?: string
}
